import { create } from 'zustand';
import { CONFIG } from '../config';
import type { StageLayout } from '@/stage/types';

export type StageSnapshot = StageLayout;

export interface HistoryState {
  undoStack: StageSnapshot[];
  redoStack: StageSnapshot[];
  canUndo: boolean;
  canRedo: boolean;

  pushState: (layout: StageSnapshot) => void;
  undo: () => StageSnapshot | null;
  redo: () => StageSnapshot | null;
  clear: () => void;
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  undoStack: [],
  redoStack: [],
  canUndo: false,
  canRedo: false,

  pushState: (layout: StageSnapshot) => {
    set((state) => {
      // Create a deep copy of the layout
      const snapshot = JSON.parse(JSON.stringify(layout));

      // Limit undo stack to MAX_HISTORY_SIZE
      const newUndoStack =
        state.undoStack.length >= CONFIG.MAX_HISTORY_SIZE
          ? [state.undoStack.slice(1), snapshot].flat()
          : [...state.undoStack, snapshot];

      return {
        undoStack: newUndoStack,
        redoStack: [], // Clear redo stack when new state is pushed
        canUndo: newUndoStack.length > 0,
        canRedo: false,
      };
    });
  },

  undo: () => {
    const state = get();

    if (state.undoStack.length === 0) {
      return null;
    }

    // Pop the most recent snapshot — this is the pre-mutation state to restore
    const restoredState = state.undoStack[state.undoStack.length - 1];
    const newUndoStack = state.undoStack.slice(0, -1);
    const newRedoStack = [...state.redoStack, restoredState];

    set({
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      canUndo: newUndoStack.length > 0,
      canRedo: true,
    });

    // Return the popped snapshot so the caller can apply it via stageStore.setLayout()
    return restoredState;
  },

  redo: () => {
    const state = get();

    if (state.redoStack.length === 0) {
      return null;
    }

    const redoedState = state.redoStack[state.redoStack.length - 1];
    const newRedoStack = state.redoStack.slice(0, -1);
    const newUndoStack = [...state.undoStack, redoedState];

    set({
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      canUndo: true,
      canRedo: newRedoStack.length > 0,
    });

    return redoedState;
  },

  clear: () => {
    set({
      undoStack: [],
      redoStack: [],
      canUndo: false,
      canRedo: false,
    });
  },
}));
